
import { IAuthorizedPageObjectTreeService, IPageController } from "../";
import { BusinessException, CatchError } from "@core/error";
import { UrlUtil } from "@core/utils";
import { BasePageController } from "../base-page-controller";
import { IResponseEvent } from "@core/data-access/model/response-event";
import { PromptTypeEnum } from "@modules/base/decorator/error/PromptType.enum";
import { IError } from "@core/data-access/model";
import { envConfig } from "@env/env-config";

export abstract class AuthorizedPageController extends BasePageController implements IPageController {

    private readonly OBJECT_TREE: IAuthorizedPageObjectTreeService;
    private readonly PROMPT_DEFAULT_CONFIG = {
        // 是否反向显示confirm和取消按钮
        reverseButtons: true,
        // 是否显示取消按钮
        showCancelButton: false,
        // 取消按钮的自定义class类
        cancelButtonClass: 'alert-login-cancel',
        // 确认按钮的自定义class类
        confirmButtonClass: 'alert-login-confirm',
        // 为按钮添加默认的swal2样式
        buttonsStyling: true,
        // 是否允许点击对话框外部来关闭对话框
        allowOutsideClick: false,
        // 是否允许按下Esc键来关闭对话框
        allowEscapeKey: false,
        title: "Permission invalid",
        content: "Go to login",
        cancelButtonText: "CANCEL",
        confirmButtonText: "OK"
    }

    public rootPath: string;
    public pageContext: qpmn.main.IPageContext;

    constructor($objectTree: IAuthorizedPageObjectTreeService) {
        super();

        this.OBJECT_TREE = $objectTree;
    }

    /**
     * Rest请求统一错误处理
     * @param e 
     */
    // @CatchError({ title: "Request Error" })
    public onRequestFail<T extends IError>(e: IResponseEvent<T>) {
        
        if (e != null && typeof e === "object") {
            if (e.status == 401) {
                window.qpAlertPrompt[PromptTypeEnum.INFO]("Missing Authentication", "You don't have authentication or it is expired, click OK to login and go on.", {
                    fn: () => {
                        // 记录当前页面地址并跳转至登录页
                        this.goToLogin();
                    }
                }, undefined, this.PROMPT_DEFAULT_CONFIG);
            } else if (e.status == 404) {
                // 
                // throw new BusinessException("A102B001");
                // window.qpAlertPrompt[PromptTypeEnum.ERROR]("Go to login?", "You are not currently logged in or the permission is invalid", {
                //     fn: () => {
                //         // 记录当前页面地址并跳转至登录页
                //         this.goToLogin();
                //     }
                // }, undefined, this.PROMPT_DEFAULT_CONFIG);
    
                window.qpAlertPrompt[PromptTypeEnum.ERROR]("System Error", `Oops! Connection failed when you were trying to pass data to the server. Please check your network connection. Alternatively, you can refresh this page and try again. If the problem still exists, please contact our technical support ${this.supportmail}`);
    
            } else if (e.status == 504) {
                window.qpAlertPrompt[PromptTypeEnum.WARNING]("Network connection timeout", "There was a network fluctuation,which caused the request to timeout. Please try again later.");
            } else if (e.status == 200 && e.data?.data?.code == "104000001") {
                // 不同Partner不可访问
                window.qpAlertPrompt[PromptTypeEnum.INFO]("Lack of permission", "You don't have permission, click [OK] to login again.", {
                    fn: () => {
                        // 不记录当前页面地址
                        // 跳转至登录页
                        this.goToLogin(false);
                    }
                }, undefined, this.PROMPT_DEFAULT_CONFIG);
            } else if (e.status == -1) {
                window.qpAlertPrompt[PromptTypeEnum.INFO]("No internet connection", "Your network is unavailable, Check your data or Wi-Fi connection.");
            } else {
                // 通用错误
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }
    }

    // @CatchError()
    public onRequestFailed<T extends IError>(e: IResponseEvent<T>): angular.IPromise<T> {
        let $translate = this.getInjectable("$translate");
        let $msgDialog = this.getInjectable("qpMessageDialog");
        let defer = this.getInjectable("$q").defer<T>();

        if (e != null && typeof e === "object") {
            //没有访问权限
            if (e.status == 401) {
                // 实名用户处理
                $msgDialog.info({
                    title: $translate.instant("MISSING_AUTHENTICATION_TITLE"),
                    content: $translate.instant("MISSING_AUTHENTICATION_CONTENT"),
                    buttons: [
                        "ok"
                    ]
                }).then(res => {
                    if (res == "ok") {
                        // 记录当前页面地址并跳转至登录页
                        this.goToLogin();
                    }
                })
            } else if (e.status == 404) {
                // 请求404报错处理
                $msgDialog.info({
                    title: $translate.instant("SYSTEM_ERROR_TITLE"),
                    content: $translate.instant("SYSTEM_ERROR_CONTENT", { supportmail: envConfig.supportEmail })
                })
            } else if (e.status == 504) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_CONNECTION_TIMEOUT_TITLE"),
                    content: $translate.instant("NETWORK_CONNECTION_TIMEOUT_CONTENT")
                })
            } else if (e.status == 200) {
                // 不同Partner不可访问
                if (e.data?.data?.code == "104000001") {
                    $msgDialog.info({
                        title: $translate.instant("LACKING_PERMISSION_TITLE"),
                        content: $translate.instant("LACKING_PERMISSION_CONTENT"),
                        buttons: [
                            "ok"
                        ]
                    }).then(res => {
                        if (res == "ok") {
                            // 不记录当前页面地址
                            // 跳转至登录页
                            this.goToLogin(false);
                        }
                    })
                } else {
                    defer.resolve(e.data.data);
                }
            } else if (e.status == -1) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_UNAVAILABLE_TITLE"),
                    content: $translate.instant("NETWORK_UNAVAILABLE_CONTENT")
                })
            } else {
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }

        return defer.promise;
    }

    /**
    * @description 检查用户页面访问权限(登录用户可访问的)
    */
    public checkAuthorization(successFunc: Function, failureFunc?: (redirectParam?: string) => void) {
        // 实名用户且未过期,则通过权限校验
        if (!this.OBJECT_TREE.isAnonymous() && !this.OBJECT_TREE.isExpire()) {
            this.authBroadcast();
            successFunc();
        } else if (!this.OBJECT_TREE.isAnonymous() && this.OBJECT_TREE.isExpire() || this.OBJECT_TREE.isAnonymous()) {
            // 匿名用户或过期的实名用户,则提示当前页面需要登录,待用户确认后前往登录页进行登录
            this.loginPrompt();
        } else {
            throw new Error("");
        }
    }

    public loginPrompt() {
        // 匿名用户则提示当前页面需要登录,待用户确认后前往登录页进行登录
        window.qpAlertPrompt[PromptTypeEnum.WARNING]("Go to login?", "You are not currently logged in or the permission is invalid", {
            fn: () => {
                // 记录当前页面地址并跳转至登录页
                this.goToLogin();
            }
        }, undefined, this.PROMPT_DEFAULT_CONFIG);
    }

    public goToLogin(isRecordCurrentPageHref: boolean = true) {
        let root: string;
        if (this.rootPath != undefined && this.rootPath != "") {
            root = this.rootPath;
        } else if (this.pageContext != undefined && this.pageContext.rootPath != undefined && this.pageContext.rootPath != "") {
            root = this.pageContext.rootPath
        } else {
            root = envConfig.appPath();
        }

        if (root.endsWith("/")) {
            root = root.slice(0, -1);
        }
        
        // 是否记录当前页面href
        if (isRecordCurrentPageHref) {
            window.location.href = root + "/partner/login/" + "?" + encodeURIComponent(location.href);
        } else {
            window.location.href = root + "/partner/login/";
        }
    }


    /**
     * @description 匿名用户提示
     * 告知用户当前页面是实名界面,需要登录后才能访问
     */
    public anonymousUserPrompt(successCallback, failHandler?, extendCfg?) {
        let _failHandler = failHandler || {};
        let _extendCfg = extendCfg || {
            // 确认按钮上的文本
            confirmButtonText: 'Confirm',
        }
        // 提示用户,当前Token失效,需要重新登录
        window.qpAlertPrompt.info("", "The current page needs to be logged in to access it.", {
            fn: successCallback
        }, _failHandler, _extendCfg);
    }
}